import { useState } from "react";
import Radio from "@mui/material/Radio";
import * as htmlToImage from "html-to-image";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import { Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useForm, Controller } from "react-hook-form";
import SendArrowIcon from "../../../../../svg/sendArrow.svg?react";
import NitroRequestService from "../../../../../utils/nitroRequest.service";
import RequestPanel from "../components/requestPanel";
import FormField from "../../../../../components/FormField";
import { useNitroStore } from "../../../../../store/Store";
import { IPinExtension } from "../../../../../types/ILocation";

interface RateRequestFormValues {
  businessOpportunity: string;
  volume: string;
  competitiveInfo: string;
  targetRate: string;
  comments?: string;
}

const RateRequestForm = ({
  selectedLocation,
  product,
  marketer,
  originCity,
  close,
  currentRate,
  rateRequestType,
  screenshotElement,
  setSendRequest,
}: {
  selectedLocation: IPinExtension;
  product: string;
  marketer: string;
  originCity: string;
  close: () => void;
  currentRate: string | number;
  screenshotElement?: HTMLDivElement | null;
  rateRequestType: string;
  setSendRequest?: (value: boolean) => void;
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RateRequestFormValues>();
  const [isSaving, setIsSaving] = useState(false);
  const [savedNotification, failedNotification] = useNitroStore((state) => [
    state.savedNotification,
    state.failedNotification,
  ]);

  const saveRateRequest = async (formValues: RateRequestFormValues) => {
    setIsSaving(true);
    try {
      if (!screenshotElement) {
        console.log("Error: The details panel reference is not available.");
        return;
      }
      const imageBlob = await htmlToImage.toBlob(screenshotElement, {
        style: {
          height: "auto",
          maxHeight: "none",
          overflow: "visible",
        },
        width: screenshotElement.scrollWidth,
        height: screenshotElement.scrollHeight,
        filter: (e) => e.className !== "modal-overlay",
      });
      const formData = new FormData();
      formData.append("type", rateRequestType);
      formData.append("marketer", marketer);
      formData.append("product", product);
      formData.append("origin", originCity);
      formData.append("destination", selectedLocation.city);
      formData.append("destinationAddress", selectedLocation.address || "");
      formData.append("receiverName", selectedLocation.owner || "");
      formData.append("businessOpportunity", formValues.businessOpportunity);
      formData.append("volume", formValues.volume);
      formData.append("competitiveInfo", formValues.competitiveInfo);
      formData.append("targetRate", formValues.targetRate);
      formData.append("comments", formValues.comments || "");
      if (imageBlob) {
        formData.append(
          "screenshot",
          imageBlob,
          "route-details-screenshot.png",
        );
      }
      await NitroRequestService.postRateRequest(formData);
      if (setSendRequest) setSendRequest(true);
      savedNotification("Rate request sent successfully");
      close();
    } catch (err) {
      console.error(err);
      failedNotification("Failed to send Rate request", (err as Error).message);
    }
    setIsSaving(false);
  };

  return (
    <>
      <div className="rate-request-modal-overlay" />
      <RequestPanel
        isSaving={isSaving}
        isLoading={false}
        goBack={close}
        title="Add Additional Context"
      >
        <div className="request-panel-container">
          <div className="request-panel-form-fields-container">
            <FormField
              label="Business Opportunity"
              textStyle={{ fontSize: 14, color: "#4B4B4B", fontWeight: 500 }}
              isRequired
            >
              <div className="business-opportunity-container">
                <div className="formFieldDetails">
                  Indicate if this is a one-time or long-term move.
                </div>

                <FormControl
                  component="fieldset"
                  error={!!errors.businessOpportunity}
                >
                  <Controller
                    name="businessOpportunity"
                    control={control}
                    defaultValue="one-time"
                    rules={{
                      required: "Business Opportunity is required",
                    }}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        <FormControlLabel
                          control={<Radio />}
                          value="one-time"
                          label="One-time move"
                          className="business-opportunity-option"
                        />
                        <FormControlLabel
                          control={<Radio />}
                          value="long-term"
                          label="Long-term"
                        />
                      </RadioGroup>
                    )}
                  />
                  {errors.businessOpportunity && (
                    <Typography variant="body2" color="error">
                      {errors.businessOpportunity.message}
                    </Typography>
                  )}
                </FormControl>
              </div>
            </FormField>

            <FormField
              label="Volume"
              isRequired
              textStyle={{
                fontSize: 14,
                color: "#4B4B4B",
                marginTop: 12,
                fontWeight: 500,
              }}
            >
              <div className="volume-container">
                <div className="formFieldDetails">
                  Indicate volume [carloads or tons] on this move.
                  <br />
                  (Wichita - 500st; Fort Dodge - 2 carloads)
                </div>

                <Controller
                  name="volume"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Volume is required",
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      error={!!errors.volume}
                      helperText={
                        errors.volume ? errors.volume.message : undefined
                      }
                    />
                  )}
                />
              </div>
            </FormField>

            <FormField
              label="Competitive Info"
              isRequired
              textStyle={{
                fontSize: 14,
                color: "#4B4B4B",
                marginTop: 12,
                fontWeight: 500,
              }}
            >
              <div className="competitive-info-container">
                <div className="formFieldDetails">
                  New Receiver, Current Sourcing, Alternate Transportation
                  Costs, etc.
                </div>

                <Controller
                  name="competitiveInfo"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Competitive Info is required",
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      error={!!errors.competitiveInfo}
                      helperText={
                        errors.competitiveInfo
                          ? errors.competitiveInfo.message
                          : undefined
                      }
                    />
                  )}
                />
              </div>
            </FormField>

            <FormField
              label="Target Rate"
              isRequired
              textStyle={{
                fontSize: 14,
                color: "#4B4B4B",
                marginTop: 12,
                fontWeight: 500,
              }}
            >
              <div className="target-rate-container">
                <div className="formFieldDetails">
                  Indicate rate level required to secure the business
                </div>

                <Controller
                  name="targetRate"
                  control={control}
                  defaultValue={currentRate.toString()}
                  rules={{
                    required: "Target Rate is required",
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      error={!!errors.targetRate}
                      helperText={
                        errors.targetRate
                          ? errors.targetRate.message
                          : undefined
                      }
                    />
                  )}
                />
              </div>
            </FormField>

            <FormField
              label="Comments"
              textStyle={{
                fontSize: 14,
                color: "#4B4B4B",
                marginTop: 12,
                fontWeight: 500,
              }}
            >
              <div className="comments-container">
                <Controller
                  name="comments"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      multiline
                      minRows={1}
                      maxRows={3}
                    />
                  )}
                />
              </div>
            </FormField>
          </div>

          {/* Spacer to push buttons to the bottom */}
          <div style={{ flexGrow: 1 }} />

          <div className="request-buttons-container">
            <span style={{ position: "absolute", left: 0 }}>
              <Button
                id="rateRequestCancelButton"
                variant="text"
                size="small"
                onClick={close}
                style={{
                  marginRight: 10,
                  border: "1px solid #8A8A8A",
                  backgroundColor: "white",
                  color: "#8A8A8A",
                  fontWeight: 500,
                  fontSize: 14,
                  font: "Inter",
                }}
              >
                Cancel
              </Button>
            </span>
            <span>
              <Button
                id="rateRequestSendButton"
                variant="contained"
                size="medium"
                onClick={handleSubmit(saveRateRequest)}
                startIcon={<SendArrowIcon />}
              >
                Send
              </Button>
            </span>
          </div>
        </div>
      </RequestPanel>
    </>
  );
};

export default RateRequestForm;
